import React from 'react';
import { cn } from '../lib/utils';
import { STATUS, ITEM_RESULT, TONE_CLASSES, DOT_CLASSES } from '../lib/compliance';

export function StatusBadge({ status, className, size = 'sm' }) {
  const s = STATUS[status] || { label: status, tone: 'neutral' };
  return (
    <span data-testid={`status-badge-${status}`} className={cn('inline-flex items-center gap-1.5 rounded-full border font-medium whitespace-nowrap', size === 'sm' ? 'px-2.5 py-0.5 text-xs' : 'px-3 py-1 text-sm', TONE_CLASSES[s.tone], className)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', DOT_CLASSES[s.tone])} />
      {s.label}
    </span>
  );
}

export function ItemResultBadge({ result, className }) {
  const s = ITEM_RESULT[result] || { label: result, tone: 'neutral' };
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap', TONE_CLASSES[s.tone], className)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', DOT_CLASSES[s.tone])} />{s.label}
    </span>
  );
}

export function Pill({ children, tone = 'neutral', className, ...rest }) {
  return <span {...rest} className={cn('inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium', TONE_CLASSES[tone], className)}>{children}</span>;
}

export function PageHeader({ title, description, actions, eyebrow }) {
  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8">
      <div className="min-w-0">
        {eyebrow && <div className="text-xs uppercase tracking-[0.14em] text-ink-400 mb-2 font-medium">{eyebrow}</div>}
        <h1 className="text-[28px] leading-tight font-semibold tracking-tight text-ink-800">{title}</h1>
        {description && <p className="mt-1.5 text-[15px] text-ink-500 max-w-2xl">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, icon: Icon, tone = 'neutral', onClick, testId, urgent = false }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} className={cn('group relative text-left rounded-xl border p-5 shadow-card transition-[box-shadow,transform] duration-200 hover:shadow-lift hover:-translate-y-0.5', onClick ? 'cursor-pointer' : 'cursor-default', urgent ? 'border-rose-300 bg-rose-600 text-white' : 'border-line bg-white')}>
      {urgent && <span className="absolute top-4 right-4 flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full rounded-full bg-white/70 animate-ping" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" /></span>}
      <div className="flex items-start justify-between">
        <div className={cn('text-[13px] font-medium', urgent ? 'text-rose-100' : 'text-ink-500')}>{label}</div>
        {Icon && <div className={cn('h-8 w-8 rounded-lg border flex items-center justify-center', urgent ? 'bg-rose-500 border-rose-400 text-white' : TONE_CLASSES[tone])}><Icon className="h-4 w-4" /></div>}
      </div>
      <div className={cn('mt-3 text-[32px] leading-none font-semibold tracking-tight tabular-nums', urgent ? 'text-white' : 'text-ink-800')}>{value}</div>
      {hint && <div className={cn('mt-2 text-xs flex items-center gap-1', urgent ? 'text-rose-100' : 'text-ink-400')}>{hint}</div>}
    </button>
  );
}

export function Panel({ title, description, actions, children, className, padded = true }) {
  return (
    <section className={cn('rounded-xl border border-line bg-white shadow-card', className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-4 px-5 py-4 border-b border-line">
          <div>
            {title && <h3 className="text-[15px] font-semibold text-ink-800">{title}</h3>}
            {description && <p className="text-[13px] text-ink-500 mt-0.5">{description}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className={padded ? 'p-5' : ''}>{children}</div>
    </section>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6">
      {Icon && <div className="h-12 w-12 rounded-xl bg-paper-100 border border-line flex items-center justify-center mb-4"><Icon className="h-5 w-5 text-ink-400" /></div>}
      <div className="text-[15px] font-semibold text-ink-700">{title}</div>
      {description && <p className="mt-1 text-sm text-ink-500 max-w-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function KV({ label, value, mono, className }) {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className="text-xs text-ink-400 font-medium">{label}</dt>
      <dd className={cn('mt-0.5 text-sm text-ink-700 break-words', mono && 'font-mono text-[13px]')}>{value ?? '—'}</dd>
    </div>
  );
}

export function Table({ columns, rows, rowKey, onRowClick, emptyText = 'Nothing to show', testId }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left">
            {columns.map((c) => <th key={c.key} className={cn('px-4 py-3 text-xs font-semibold uppercase tracking-wider text-ink-400 whitespace-nowrap', c.className)}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-ink-400">{emptyText}</td></tr>}
          {rows.map((r, i) => (
            <tr key={rowKey ? rowKey(r) : i} onClick={onRowClick ? () => onRowClick(r) : undefined} className={cn('border-b border-line/70 last:border-0 transition-colors duration-150', onRowClick && 'cursor-pointer hover:bg-paper-100')}>
              {columns.map((c) => <td key={c.key} className={cn('px-4 py-3 align-middle text-ink-700', c.className)}>{c.render ? c.render(r) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SectionLabel({ children }) {
  return <div className="text-xs uppercase tracking-[0.12em] text-ink-400 font-semibold mb-3">{children}</div>;
}
